import { useEffect, useState } from "react";
import { Lock, Pencil, Check, Plus, X, ShieldAlert } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useBlockLease } from "@/hooks/useBlockLease";
import { useAdminReleaseLease } from "@/lib/workDocumentQueries";
import { useMe } from "@/lib/rehearsalRoomQueries";
import AutosaveStatus from "../presentation/AutosaveStatus";
import { useRoomBlockRegistry } from "./RoomBlockRegistry";

function parseChecklistItems(text) {
  try {
    const parsed = JSON.parse(text || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Master v5 Prompt 9 — one Room block, reusing the exact accepted
 * Prompt 8A lease/autosave engine (useBlockLease) unchanged. Only the
 * editing widget varies by `block.block_type`:
 *  - "checklist" -> checkbox rows (backed by a JSON array in the SAME
 *    `content.text` string field the engine already saves/leases/
 *    versions — no new backend contract, just a different renderer of
 *    the existing content field);
 *  - anything else (e.g. "long_text") -> a plain textarea, identical to
 *    WorkDocumentDetail's own BlockCard.
 *
 * Registers/unregisters its `stopEditing` and live `dirty` state with
 * the Room's exit registry so a controlled "Save & Exit" can flush every
 * open lease before navigating, and so navigation-blocking only ever
 * reacts to genuinely unsaved local state.
 */
export default function RoomBlockField({ workspaceId, docId, block, isViewAsClient, isInstanceLocked }) {
  const lease = useBlockLease({ workspaceId, docId, serverBlock: block, isViewAsClient });
  const { register, unregister, setBlockDirty } = useRoomBlockRegistry();
  const { data: me } = useMe();
  const isCoach = me?.portal_role === "coach_admin" && !isViewAsClient;
  const adminRelease = useAdminReleaseLease(workspaceId, docId);
  const blockId = block.block_id;

  useEffect(() => {
    if (lease.isMine) register(blockId, lease.stopEditing);
    else unregister(blockId);
    return () => unregister(blockId);
  }, [lease.isMine, lease.stopEditing, blockId, register, unregister]);

  useEffect(() => {
    setBlockDirty(blockId, lease.dirty);
    return () => setBlockDirty(blockId, false);
  }, [lease.dirty, blockId, setBlockDirty]);

  const readOnly = isInstanceLocked || isViewAsClient || (!lease.isMine && !block.editable && block.state !== "editing_by_other");
  const isChecklist = block.block_type === "checklist";

  return (
    <div
      className={`border rounded-lg p-4 ${lease.isMine ? "border-terracotta bg-terracotta/5" : "border-ink/15 bg-white/50"}`}
      data-testid={`room-block-${blockId}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs uppercase tracking-wide text-ink-soft">{block.block_key.replace(/-/g, " ")}</p>
        {lease.isMine && lease.saveState !== "idle" && (
          <AutosaveStatus status={lease.saveState === "locked" ? "error" : lease.saveState} testId={`room-block-${blockId}-autosave-status`} />
        )}
      </div>

      {block.state === "editing_by_other" && (
        <div className="mb-2" data-testid={`room-block-${blockId}-locked-message`}>
          <p className="text-sm text-mustard-dark italic">Another user is editing this section right now</p>
          {isCoach && (
            <button
              onClick={async () => {
                const reason = window.prompt("Reason for releasing this lock (visible in the audit log):");
                if (!reason) return;
                try {
                  await adminRelease.mutateAsync({ blockId, reason });
                  toast.success("Lock released");
                } catch (err) {
                  toast.error(err.body?.detail || "Couldn't release this lock");
                }
              }}
              disabled={adminRelease.isPending}
              className="mt-1 inline-flex items-center gap-1 text-xs text-terracotta/80 hover:text-terracotta underline disabled:opacity-50"
              data-testid={`room-block-${blockId}-admin-release-btn`}
            >
              <ShieldAlert size={11} /> Release this lock (admin)
            </button>
          )}
        </div>
      )}

      {lease.recoverableDraft && !lease.isMine && (
        <div className="border border-mustard/50 bg-mustard/10 rounded-md p-3 mb-3 text-xs" data-testid={`room-block-${blockId}-recovery-banner`}>
          <p className="text-ink mb-2">You have unsaved text from an earlier session that was never confirmed as saved.</p>
          <div className="flex items-center gap-3">
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.keepLocalDraft} className="font-medium text-terracotta hover:text-terracotta/80" data-testid={`room-block-${blockId}-recovery-keep`}>
              Restore my draft
            </button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.discardLocalDraft} className="text-ink-soft hover:text-ink" data-testid={`room-block-${blockId}-recovery-discard`}>
              Discard, use latest
            </button>
          </div>
        </div>
      )}

      {lease.saveState === "conflict" && (
        <div className="border border-mustard/50 bg-mustard/10 rounded-md p-3 mb-3 text-xs" data-testid={`room-block-${blockId}-conflict-banner`}>
          <p className="text-ink mb-2">Someone else saved a newer version of this section. Your text was not lost — choose what to do:</p>
          <button onMouseDown={(e) => e.preventDefault()} onClick={lease.acceptLatestOnConflict} className="font-medium text-terracotta hover:text-terracotta/80" data-testid={`room-block-${blockId}-conflict-view-latest`}>
            View latest (discard my edit)
          </button>
        </div>
      )}

      {isChecklist ? (
        <ChecklistEditor lease={lease} readOnly={readOnly} blockId={blockId} />
      ) : (
        <TextEditor lease={lease} readOnly={readOnly} blockId={blockId} block={block} />
      )}

      <div className="flex items-center gap-2 mt-2">
        {readOnly && block.state !== "editing_by_other" && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-soft" data-testid={`room-block-${blockId}-readonly-badge`}>
            <Lock size={11} /> Read-only
          </span>
        )}
        {!readOnly && !lease.isMine && block.state !== "editing_by_other" && (
          <button onClick={lease.startEditing} className="inline-flex items-center gap-1 text-xs text-terracotta hover:text-terracotta/80" data-testid={`room-block-${blockId}-start-edit`}>
            <Pencil size={11} /> Click to edit
          </button>
        )}
        {lease.isMine && (
          <button onClick={lease.stopEditing} className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink" data-testid={`room-block-${blockId}-done-edit`}>
            <Check size={11} /> Done editing
          </button>
        )}
      </div>
    </div>
  );
}

function TextEditor({ lease, readOnly, blockId }) {
  if (lease.isMine) {
    return (
      <Textarea
        value={lease.localText}
        onChange={(e) => lease.onChangeText(e.target.value)}
        onBlur={() => lease.stopEditing()}
        autoFocus
        rows={5}
        className="bg-white"
        data-testid={`room-block-${blockId}-editor`}
      />
    );
  }
  return (
    <div
      onClick={() => !readOnly && lease.startEditing()}
      className={`text-sm text-ink whitespace-pre-wrap min-h-[3rem] rounded-md px-2 py-1.5 -mx-2 ${!readOnly ? "cursor-text hover:bg-ink/5" : ""}`}
      data-testid={`room-block-${blockId}-preview`}
    >
      {lease.localText || <span className="text-ink-soft italic">Empty — {readOnly ? "read-only" : "click to write"}</span>}
    </div>
  );
}

function ChecklistEditor({ lease, readOnly, blockId }) {
  const [newItemText, setNewItemText] = useState("");
  const items = parseChecklistItems(lease.localText);
  const canEdit = lease.isMine;

  function commit(nextItems) {
    lease.onChangeText(JSON.stringify(nextItems));
  }

  function toggleItem(itemId) {
    commit(items.map((it) => (it.id === itemId ? { ...it, checked: !it.checked } : it)));
  }

  function addItem() {
    const text = newItemText.trim();
    if (!text) return;
    commit([...items, { id: `item-${Date.now()}`, text, checked: false }]);
    setNewItemText("");
  }

  function removeItem(itemId) {
    commit(items.filter((it) => it.id !== itemId));
  }

  if (items.length === 0 && !canEdit) {
    return <p className="text-sm text-ink-soft italic" data-testid={`room-block-${blockId}-checklist-empty`}>No checklist items yet.</p>;
  }

  return (
    <div className="space-y-2" data-testid={`room-block-${blockId}-checklist`}>
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2" data-testid={`room-block-${blockId}-checklist-item-${item.id}`}>
          <Checkbox
            checked={!!item.checked}
            disabled={!canEdit}
            onCheckedChange={() => toggleItem(item.id)}
            data-testid={`room-block-${blockId}-checklist-item-${item.id}-checkbox`}
          />
          <span className={`text-sm text-ink flex-1 ${item.checked ? "line-through text-ink-soft" : ""}`}>{item.text}</span>
          {canEdit && (
            <button onClick={() => removeItem(item.id)} className="text-ink-soft hover:text-terracotta" data-testid={`room-block-${blockId}-checklist-item-${item.id}-remove`}>
              <X size={13} />
            </button>
          )}
        </div>
      ))}
      {canEdit && (
        <div className="flex items-center gap-2 pt-1">
          <Input
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            placeholder="Add a checklist item…"
            className="bg-white text-sm h-8"
            data-testid={`room-block-${blockId}-checklist-add-input`}
          />
          <button onClick={addItem} className="text-terracotta hover:text-terracotta/80" data-testid={`room-block-${blockId}-checklist-add-btn`}>
            <Plus size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
